import { useDeferredValue, useEffect, useState } from "react";
import { api } from "../../api/api";
import { NavBar } from "../../components/NavBar";
import { extractPixValue, maskCurrency } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { FaSearch } from "react-icons/fa";
import "./style.css";

export function HomeHome() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const normalizedSearch = deferredSearch.trim().toLocaleLowerCase("pt-BR");
  const filteredCourses = courses.filter((course) => {
    if (!normalizedSearch) return true;

    return `${course.name ?? ""} ${course.description ?? ""}`
      .toLocaleLowerCase("pt-BR")
      .includes(normalizedSearch);
  });

  async function getCourses() {
    const response = await api.get("/api/v1/courses?active=true");
    const currentDate = DateTime.local();

    const dataCourse = response.data.data.map((course) => {
      const startDateDiff = DateTime.fromSQL(course.startDate).diff(
        currentDate,
        "days",
      ).days;

      return {
        ...course,
        isHideButton: course.active !== true || startDateDiff < 0,
      };
    });

    setCourses(
      dataCourse.sort(
        (a, b) => Number(a.isHideButton) - Number(b.isHideButton),
      ),
    );
    setLoading(false);
  }

  useEffect(() => {
    getCourses();
  }, []);

  return (
    <>
      <NavBar>
        <div>
          <div className="page-heading">
            <div>
              <h1>Nossos cursos</h1>
              <p>Escolha o curso que mais combina com os seus objetivos.</p>
            </div>
          </div>

          <div className="course-search surface-card">
            <FaSearch aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Pesquise pelo nome do curso"
              aria-label="Pesquisar cursos"
            />
            {!loading && (
              <span>{filteredCourses.length} {filteredCourses.length === 1 ? "curso" : "cursos"}</span>
            )}
          </div>

          {loading && (
            <div className="empty-state surface-card">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden"></span>
              </div>
              <p className="mt-3 mb-0">Carregando cursos...</p>
            </div>
          )}
          <div className="courses-grid">
            {!loading &&
              filteredCourses.map((course) => {
                return (
                  <article
                    key={course.id}
                    className="course-card surface-card"
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/courses/${course.id}`)}
                    onKeyDown={(event) => {
                      if (
                        event.currentTarget === event.target &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        navigate(`/courses/${course.id}`);
                      }
                    }}
                  >
                    <div className="course-cover">
                      <img
                        src={course.url || "img/curso-virtual-jpg.jpg"}
                        alt={course?.name}
                      />
                    </div>
                    <div className="course-body">
                      <span className={`course-kicker ${course.isHideButton ? "closed" : "open"}`}>
                        {course.isHideButton ? "Matrículas encerradas" : "Matrículas abertas"}
                      </span>
                      <h2>{course?.name}</h2>
                      <p className="course-description">
                        {String(course.description).substring(0, 180) + "..."}
                      </p>
                      <div className="course-details">
                        {course.periodo && (
                          <div className="course-detail">
                            <span>Período</span>
                            <strong>{course.periodo}</strong>
                          </div>
                        )}
                        {course.duration && (
                          <div className="course-detail">
                            <span>Duração</span>
                            <strong>{course.duration}</strong>
                          </div>
                        )}
                        <div className="course-detail">
                          <span>Início</span>
                          <strong>
                            {DateTime.fromSQL(course.startDate).toFormat(
                              "dd/MM/yyyy",
                            )}
                          </strong>
                        </div>
                        <div className="course-detail">
                          <span>Término</span>
                          <strong>
                            {DateTime.fromSQL(course.endDate).toFormat(
                              "dd/MM/yyyy",
                            )}
                          </strong>
                        </div>
                        <div className="course-detail">
                          <span>Curso à vista</span>
                          <strong>{maskCurrency(course.pricingTotal)}</strong>
                        </div>
                        <div className="course-detail">
                          <span>Curso parcelado</span>
                          <strong>
                            {course.qtdInstallments}x de{" "}
                            {maskCurrency(course.pricingInstallments)}
                          </strong>
                        </div>
                        <div className="course-detail">
                          <span>Material à vista</span>
                          <strong>
                            {maskCurrency(course.materialPriceOmega)}
                          </strong>
                        </div>
                        <div className="course-detail">
                          <span>Material parcelado</span>
                          <strong>
                            {course.qtdInstallmentsMaterialOmega}x de{" "}
                            {maskCurrency(
                              course.pricingInstallmentsMaterialOmega,
                            )}
                          </strong>
                        </div>
                        <div className="course-detail">
                          <span>Matrícula</span>
                          <strong>
                            {maskCurrency(extractPixValue(course.pix))}
                          </strong>
                        </div>
                      </div>
                      {!course.isHideButton && (
                        <button
                          className="btn btn-primary course-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/subscription?id=${course.id}`)
                          }}
                        >
                          Quero me matricular
                        </button>
                      )}
                      {course.isHideButton && (
                        <span className="course-closed">
                          Período de matrícula encerrado
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
          </div>
          {!loading && courses.length === 0 && (
            <div className="empty-state surface-card">
              Nenhum curso disponível no momento.
            </div>
          )}
          {!loading && courses.length > 0 && filteredCourses.length === 0 && (
            <div className="empty-state surface-card course-no-results">
              Nenhum curso encontrado para <strong>“{search}”</strong>.
            </div>
          )}
        </div>
      </NavBar>
    </>
  );
}
